import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, X } from 'lucide-react';
import { WhatsAppTemplateSelector } from './WhatsAppTemplateSelector';
import type { WhatsAppErrorState } from '@/types/message-types';
import { getStatusBadgeVariant } from '@/lib/status-appearance';

interface WhatsAppErrorPanelProps {
  error: WhatsAppErrorState | null;
  conversationId: string;
  customerPhone?: string;
  showTemplateSelector: boolean;
  onShowTemplateSelector: (show: boolean) => void;
  onClearError: () => void;
  onTemplateSelect?: () => void;
}

export function WhatsAppErrorPanel({
  error,
  conversationId,
  customerPhone,
  showTemplateSelector,
  onShowTemplateSelector,
  onClearError,
  onTemplateSelect
}: WhatsAppErrorPanelProps) {
  const { t } = useTranslation('conversations');

  if (!error && !showTemplateSelector) {
    return null;
  }

  // The emoji is inline message text, not a paintable icon component the shared module's
  // icon map deals in (STATUS_ICONS only maps Lucide components), so it stays local.
  const getErrorIcon = () => {
    switch (error?.type) {
      case 'closed_window':
        return '🔒';
      case 'rate_limit':
        return '⏱️';
      case 'invalid_template':
        return '📄';
      case 'api_error':
      default:
        return '❌';
    }
  };

  // A message-send error's own type vocabulary (not an object's lifecycle status), now
  // resolved off the shared module's `message_error_type` entity instead of a local switch.
  // `Alert` only supports 'default' | 'destructive', which is exactly the pair this entity
  // maps to, so the module's result is narrowed rather than passed through as-is.
  const getErrorVariant = (): 'default' | 'destructive' =>
    (getStatusBadgeVariant('message_error_type', error?.type ?? 'api_error') as 'default' | 'destructive' | undefined) ?? 'destructive';

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <Alert variant={getErrorVariant()}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>{getErrorIcon()}</span>
              <span>{error.message}</span>
            </div>
            
            <div className="flex items-center gap-2">
              {error.requiresTemplate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onShowTemplateSelector(true)}
                >
                  Select Template
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearError}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Template Selector */}
      {showTemplateSelector && customerPhone && (
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium">{t('whatsappErrorPanel.selectTemplateTitle')}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onShowTemplateSelector(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <WhatsAppTemplateSelector
            conversationId={conversationId}
            customerPhone={customerPhone}
            onTemplateSent={() => {
              onShowTemplateSelector(false);
              onTemplateSelect?.();
            }}
            onCancel={() => onShowTemplateSelector(false)}
          />
        </div>
      )}
    </div>
  );
}